import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useConfirm } from '../confirm/ConfirmDialogProvider';
import { 
  Users, 
  Calendar, 
  LayoutDashboard, 
  Upload, 
  LogOut,
  Menu,
  X,
  Building2,
  Activity,
  Handshake,
  ClipboardList,
  UserRound,
  FileText
} from 'lucide-react';

interface AdminLayoutProps {
  children: React.ReactNode;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { confirm } = useConfirm();
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = React.useState(false);

  React.useEffect(() => {
    setIsMobileSidebarOpen(false);
  }, [location.pathname]);

  React.useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setIsMobileSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLogout = async () => {
    const shouldLogout = await confirm({
      title: 'Cerrar sesión',
      message: '¿Seguro que deseas cerrar sesión?',
      confirmText: 'Cerrar sesión',
      cancelText: 'Cancelar',
      variant: 'danger',
    });

    if (!shouldLogout) return;

    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    }
  };

  const menuItems = [
    {
      path: '/admin/assemblies',
      label: 'Asambleas',
      icon: Calendar,
      description: 'Gestión de asambleas'
    },
    {
      path: '/admin/dashboard',
      label: 'Dashboard',
      icon: LayoutDashboard,
      description: 'Dashboard en vivo'
    },
    {
      path: '/admin/users',
      label: 'Usuarios',
      icon: Users,
      description: 'Gestión de usuarios'
    },
    {
      path: '/admin/complexes',
      label: 'Conjuntos',
      icon: Building2,
      description: 'Conjuntos residenciales'
    },
    {
      path: '/admin/delegations',
      label: 'Delegaciones',
      icon: Handshake,
      description: 'Poderes y delegaciones'
    },
    {
      path: '/admin/powers',
      label: 'Poderes firmados',
      icon: FileText,
      description: 'Descarga de poderes PDF'
    },
    {
      path: '/admin/outreach',
      label: 'Seguimiento',
      icon: ClipboardList,
      description: 'Contacto sin poder'
    },
    {
      path: '/admin/import',
      label: 'Importar',
      icon: Upload,
      description: 'Importar usuarios'
    },
    {
      path: '/admin/queue',
      label: 'Colas',
      icon: Activity,
      description: 'Gestión de colas'
    },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <div className="h-screen bg-slate-50 flex overflow-hidden">
      {/* Mobile backdrop */}
      {isMobileSidebarOpen && (
        <button
          type="button"
          aria-label="Cerrar menú"
          className="fixed inset-0 z-40 bg-slate-950/35 backdrop-blur-[1px] md:hidden"
          onClick={() => setIsMobileSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 bg-white border-r border-slate-200 transition-transform duration-300 h-full flex-shrink-0 md:static md:z-auto md:translate-x-0 md:transition-[width] md:duration-300 ${
          isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
        } ${isSidebarOpen ? 'md:w-64' : 'md:w-20'} w-80 max-w-[86vw] md:max-w-none`}
      >
        <div className="h-full flex flex-col overflow-hidden">
          {/* Header */}
          <div className="p-4 border-b border-slate-200 flex items-center justify-between gap-3">
            {isSidebarOpen && (
              <h1 className="text-xl font-bold text-slate-800 whitespace-nowrap">Admin Panel</h1>
            )}
            <div className="ml-auto flex items-center gap-2">
              <button
                type="button"
                onClick={() => setIsMobileSidebarOpen(false)}
                className="inline-flex md:hidden p-2 rounded-lg hover:bg-slate-100 text-slate-600"
                aria-label="Cerrar panel"
              >
                <X size={20} />
              </button>
              <button
                type="button"
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                className="hidden md:inline-flex p-2 rounded-lg hover:bg-slate-100 text-slate-600"
                aria-label={isSidebarOpen ? 'Contraer panel' : 'Expandir panel'}
              >
                {isSidebarOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsMobileSidebarOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-2xl transition-colors ${
                    active
                      ? 'bg-blue-50 text-blue-700 font-medium shadow-sm'
                      : 'text-slate-700 hover:bg-slate-50'
                  }`}
                  title={!isSidebarOpen ? item.label : undefined}
                >
                  <Icon size={20} className={active ? 'text-blue-700' : 'text-slate-500'} />
                  {isSidebarOpen && (
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{item.label}</div>
                      <div className="text-xs text-slate-500 truncate">{item.description}</div>
                    </div>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* User Info & Logout */}
          <div className="p-4 border-t border-slate-200">
            {isSidebarOpen ? (
              <div className="space-y-3">
                <div className="px-4 py-3 bg-slate-50 rounded-2xl border border-slate-200">
                  <div className="font-medium text-sm text-slate-800">
                    {user?.firstName} {user?.lastName}
                  </div>
                  <div className="text-xs text-slate-500 break-all">{user?.email}</div>
                  {user?.apartment && (
                    <div className="text-xs text-slate-500 mt-1">
                      Apto: {user.apartment}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => navigate('/profile')}
                  className="flex w-full items-center gap-2 rounded-xl px-4 py-2 text-sm text-slate-700 transition-colors hover:bg-slate-50"
                >
                  <UserRound size={16} />
                  Mi perfil
                </button>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-xl transition-colors"
                >
                  <LogOut size={16} />
                  Cerrar Sesión
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => navigate('/profile')}
                  className="w-full rounded-xl p-2 text-slate-600 transition-colors hover:bg-slate-50"
                  title="Mi perfil"
                >
                  <UserRound size={20} />
                </button>
                <button
                  onClick={handleLogout}
                  className="w-full p-2 text-red-600 hover:bg-red-50 rounded-xl transition-colors"
                  title="Cerrar Sesión"
                >
                  <LogOut size={20} />
                </button>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="relative flex-1 overflow-y-auto">
        <button
          type="button"
          onClick={() => setIsMobileSidebarOpen(true)}
          className="fixed left-4 top-4 z-30 inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white/90 px-3 py-2 text-sm font-medium text-slate-700 shadow-sm backdrop-blur md:hidden"
        >
          <Menu size={18} />
          Menú
        </button>
        <div className="h-full">
          {children}
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
